
import CVacancyDetail from '@/modules/auth/components/candidate/c-vacancy-detail/c-vacancy-detail.component.vue'
import GetUseCase from '@/modules/vacancy/usecases/get.usecase'
import { defineComponent, ref } from 'vue'
import { FilterKey, LocalizationPath, VacancyGetters } from '@tokyojob/core'
import { FilterQueryManager, getLocalization } from '@tokyojob/frontend-components'
import {
  decodeJwt,
  getJapaneseLanguageListStore,
  IHead,
  LocalizationService,
  UserFactory,
  VacancyDetailsMetaFactory,
  VacancyHasAppliedComposable
} from '@tokyojob/frontend-core'
import { Layout } from '~/base/utils/constants/layout'
import authenticatedOptional from '~/middleware/authenticated-optional'
import CVacancyRecommendations from '~/modules/app/components/c-vacancy-recommendations/c-vacancy-recommendations.component.vue'
import VisaTypeHierarchyGetUsecase from '~/modules/category/usecase/visa-type-hierarchy-get.usecase'
import { getAppStore } from '~/store/app.store'
import { getProvidedApp } from '~/base/utils/provided-getter'

export default defineComponent({
  layout: Layout.Home,
  middleware: [authenticatedOptional],
  components: { CVacancyDetail, CVacancyRecommendations },
  async asyncData({ App, error, params, redirect }) {
    const language = App.localizationSelector.language
    const locale = App.localizationSelector.languageLocale
    const filterQueryManager = new FilterQueryManager()
    // Get visa data from cookie, return empty array if not found
    const userFactory = new UserFactory()
    const appStore = getAppStore()
    const jpStore = getJapaneseLanguageListStore()
    const user = appStore.profile && appStore.profile.id ? appStore.profile : userFactory.newAnonymousUser(locale)
    const visaData = filterQueryManager.getDataFromCookie(FilterKey.visa, App)
    const jpLevelCookie = filterQueryManager.getDataFromCookie(FilterKey.japanese, App)
    // Get user profile if logged in
    if (App.state.isLoggedInCandidate) {
      // if user has a visa, set the visa type path
      if (user.candidate?.candidateVisa?.visaTypePath) {
        visaData.path = user.candidate?.candidateVisa?.visaTypePath
      }
    } else {
      if (visaData.path) {
        user.candidate = { candidateVisa: { visaTypePath: visaData.path } }
      }
      if (jpLevelCookie.path) {
        const currentJpLevel = jpStore.items[jpLevelCookie.path[0]]
        user.candidate = {
          ...user.candidate,
          japaneseConversationId: jpLevelCookie.path[0],
          japaneseConversation: currentJpLevel
        }
      }
    }
    const visaTypeId = filterQueryManager.getBottomItem(visaData.path)

    const useCase = new GetUseCase(App)
    const vacancy = await useCase.execute({ id: params.id, candidateIsLoggedIn: false, visaTypeId, locale })
    if (!vacancy) {
      error({ message: 'Vacancy does not exist', statusCode: 404 })
      return
    }
    if (!vacancy?.translationIsAvailable) redirect(`/job/${vacancy?.id}/translation-not-available?lang=${language}`)

    const visaTypeHierarchyUseCase = new VisaTypeHierarchyGetUsecase(App)
    const visaTypesHierarchy = await visaTypeHierarchyUseCase.execute(locale)

    const item = vacancy!
    const applicationId = undefined
    const applyInvalidReason = vacancy?.applyInvalidReason || []
    const { hasApplied } = VacancyHasAppliedComposable()
    const alreadyApplied = hasApplied(parseInt(params.id))
    const visaPath = user?.candidate?.candidateVisa?.visaTypePath
    const jpLevel = user?.candidate?.japaneseConversationId
    const recommendedJobs = item?.recommendedJobs || []

    return {
      item,
      user,
      applicationId,
      applyInvalidReason,
      alreadyApplied,
      visaTypesHierarchy,
      recommendedJobs,
      visaPath,
      jpLevel
    }
  },
  setup() {
    const localization = getLocalization(getProvidedApp())

    return {
      item: {},
      user: null,
      applicationId: undefined,
      applyInvalidReason: [],
      alreadyApplied: false,
      visaTypesHierarchy: {},
      recommendedJobs: [],
      localization
    }
  },
  head(): IHead {
    const localization = getLocalization(this.App)
    const titleString = LocalizationService.t<string>(localization, LocalizationPath.VacancyDetailPageMetaTagTitle)
    const vacancy = this.item
    const title = VacancyGetters.getSEOTitle(vacancy, titleString)
    const meta = VacancyDetailsMetaFactory.metaTags(this.localization, vacancy, this.App.localizationSelector.languageLocale)
    return { title, meta }
  }
})
